import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useStore } from '../store/useStore'
import { useEnrollmentGate, markEnrolled, markTotpEnrolled, resetEnrollmentGate } from '../hooks/useEnrollmentGate'
import { useDeviceConfirmGate, markDeviceConfirmed } from '../hooks/useDeviceConfirmGate'
import { useDeviceApprovalGate, recheckDeviceApproval } from '../hooks/useDeviceApprovalGate'
import { useDevicePendingApprovals } from '../hooks/useDevicePendingApprovals'
import { useWebAuthn } from '../hooks/useWebAuthn'
import PasskeyEnrollmentModal from './PasskeyEnrollmentModal'
import TotpEnrollmentModal from './TotpEnrollmentModal'
import DeviceConfirmModal from './DeviceConfirmModal'
import DeviceApprovalWaitingModal from './DeviceApprovalWaitingModal'
import DeviceApprovalPromptModal from './DeviceApprovalPromptModal'

/**
 * Auth + #734 WebAuthn enrollment gate + #791 ② 新規 device 本人確認 gate.
 *
 * 1. 未ログイン → /login
 * 2. ログイン済 + **未確認の新規 device 登録あり** (#791 ②) →
 *    DeviceConfirmModal で全画面 block (= ATO 検知。security 最優先で
 *    enrollment より先に判定)。
 *    ただし `/profile/passkeys` (= passkey 設定/失効画面) では本 gate を
 *    **skip** する (#802): modal の「心当たりがない」誘導先がこの画面なので、
 *    ここまで gate で塞ぐと revoke に到達できなくなるため。
 * 3. ログイン済 + enrollment 必須 (`required_*` mode + credentials 0) →
 *    PasskeyEnrollmentModal で全画面 block (= URL 直打ち bypass 防止)
 * 4. ログイン済 + どちらも不要 → children を通常 render
 */
/**
 * #1246 allowGuest: ゲスト閲覧を許可するページ (例: /home) 用。未ログインでも
 * children を描画する。session 復元待ち (restoreWaited) はそのまま効かせるので、
 * ログイン済みユーザーの full reload 時にゲスト UI が一瞬見える事故は起きない。
 * ログイン済みユーザーに対する device/enrollment gate 群は従来どおり全て通る。
 */
/**
 * 国籍が未回答でも**止めない**画面。
 *
 * - `/setup/profile` — 聞く場所そのもの
 * - `/charge/*` — Stripe からの戻り先 (`/charge/return`) や結果表示。ここで
 *   割り込むと**決済の結果が画面から消える**。金が動いた後なので最悪
 * - `/payment/*` — 同じ理由 (支払い完了画面)
 *
 * 除外しても、次に普通の画面へ行けば聞かれる。
 */
const PROFILE_GATE_EXEMPT = ['/setup/', '/charge/', '/payment/']

/** 聞いた後で元の場所へ戻すために置いておく。Login の postLoginRedirect と同じ仕組み。 */
export const PROFILE_GATE_REDIRECT_KEY = 'profileGateRedirect'

export default function RequireAuth({ children, allowGuest }: { children: React.ReactNode; allowGuest?: boolean }) {
  const isLoggedIn = useStore((s) => s.isLoggedIn)
  const user = useStore((s) => s.user)
  const location = useLocation()
  const { requiresConfirmation, pending, loading: dcLoading } = useDeviceConfirmGate()
  const { requiresEnrollment, loading: enLoading } = useEnrollmentGate()
  // #815: WebAuthn 不可ブラウザ判定 + TOTP fallback flag。capable / flag OFF では
  // 従来どおり PasskeyEnrollmentModal（挙動不変）。
  const { supported, config } = useWebAuthn()
  // #819 Phase 2b: 初見デバイス承認 gate (flag deviceApprovalEnabled、既定 OFF)。
  // flag は config 由来。!devApprovalOn では hook が fetch/poll/block を
  // 一切しない = byte-for-byte 不変。
  const devApprovalOn = config?.deviceApprovalEnabled === true
  const {
    requiresApproval,
    trusted: approvalTrusted,
    denied: approvalDenied,
    loading: approvalLoading,
  } = useDeviceApprovalGate(devApprovalOn)
  // /pending は trusted device からのみ (backend 403)。trusted 時だけ fetch。
  const { pending: devicePending, refresh: refreshPending } =
    useDevicePendingApprovals(devApprovalOn && approvalTrusted)
  // #838: capable 端末で passkey ceremony が失敗した user が TOTP へ退避
  // したか。escape は **失敗後の明示操作**のみで立つ(#789: 事前対等選択
  // にしない)。totpFallbackEnabled OFF では escape 自体出ない=不変。
  const [escapedToTotp, setEscapedToTotp] = useState(false)

  // #1189 Phase 3: Stripe LPM redirect 復帰時の /login 誤 redirect 対策。
  //
  // full page load 直後は zustand isLoggedIn=false のまま App.tsx の
  // restoreSession が非同期実行中で、この時点で /login に redirect すると
  // Stripe redirect 復帰の /charge/return が login 画面で潰される。
  // localStorage.isLoggedIn='true' の間は「session 復元中」とみなし、
  // 短時間 (最大 3 秒) 待って復元完了を確認する。
  //
  // 初期値は localStorage を見て lazy init: 未認証なら true (即 redirect 可)、
  // 認証形跡ありなら false (spinner で復元完了を待つ)。3 秒 timeout で諦めて
  // redirect (backend 障害等で復元がハングした時の安全弁)。
  const [restoreWaited, setRestoreWaited] = useState(() => {
    if (typeof window === 'undefined') return true
    return localStorage.getItem('isLoggedIn') !== 'true'
  })
  useEffect(() => {
    if (isLoggedIn) return
    if (restoreWaited) return
    const timer = window.setTimeout(() => setRestoreWaited(true), 3000)
    return () => window.clearTimeout(timer)
  }, [isLoggedIn, restoreWaited])

  if (!isLoggedIn) {
    if (!restoreWaited) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-warm-white">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-yokoso-red border-t-transparent" />
        </div>
      )
    }
    if (allowGuest) return <>{children}</>
    // #1246: ログイン後に元のページへ復帰させる (ゲストがタブバーから
    // /wallet 等を踏んだケース)。Login.tsx の既存消費ロジックがそのまま拾う。
    sessionStorage.setItem('postLoginRedirect', location.pathname + location.search)
    return <Navigate to="/login" replace />
  }

  // #802: passkey 設定画面は device-confirm gate の対象外。
  // (= 未確認 device がある時の「心当たりがない→設定で失効」導線を確保。
  //  この画面自体が credential 管理/失効の場なので gate で塞ぐ意味がない)。
  const isPasskeySettings = location.pathname === '/profile/passkeys'

  // #791 ②: 未確認の新規 device 登録 → 本人確認を最優先で強制
  // (= attacker silent 登録の可能性、enrollment より先に gate)。
  if (!isPasskeySettings && !dcLoading && requiresConfirmation) {
    return (
      <DeviceConfirmModal
        open
        pending={pending}
        onConfirmed={() => {
          markDeviceConfirmed()
          // revoke で全 credential 失効した場合に enrollment gate を再評価させる
          // (= 0 creds + required_* → 再登録強制へ自然遷移)。
          resetEnrollmentGate()
        }}
      />
    )
  }

  // #819 Phase 2b: 初見デバイス承認 gate。**flag deviceApprovalEnabled が ON
  // のときだけ**(既定 OFF = 配線されても挙動 byte-for-byte 不変)。#791② の
  // 後・enrollment の前(未信頼の新端末は enroll させない)。
  // risk-tier (#819 Phase3 訂正): TOTP 2FA 済は Firebase sign-in で強要素を
  // 既にクリアしているので hard-block しない(trusted 端末喪失時に TOTP
  // ユーザーを締め出さない)。passkey のみ層が主対象。
  // /profile/passkeys carve-out は不要: 本 gate の deny は in-modal 確認で
  // 完結し設定画面へ誘導しない。未信頼端末は設定画面も塞ぐのが正。
  if (devApprovalOn) {
    // 否認済端末は TOTP 有無に関わらず block (明示的に拒否された端末)。
    if (approvalDenied) {
      return (
        <DeviceApprovalWaitingModal
          open
          denied
          onRecheck={() => recheckDeviceApproval()}
        />
      )
    }
    // 未信頼端末の承認待ち block。
    // #869c: TOTP 強要素免除(`!hasTotpFactor` 条件)は撤回。代わりに
    // TOTP refined-(a)(MFA 通過時に `viaTotp=true` で register_device →
    // backend が自動 trusted 化)で lockout 回避する設計に統一。
    // 結果: TOTP user も新端末では一瞬 waiting modal が出る可能性があるが
    // register_device の auto-trust + 4s poll で即解除される。WebAuthn
    // refined-(a) と対称な挙動。memory `project_device_approval_gate_status`。
    if (requiresApproval && !approvalLoading) {
      return (
        <DeviceApprovalWaitingModal
          open
          denied={false}
          onRecheck={() => recheckDeviceApproval()}
        />
      )
    }
    // trusted 端末 + 承認待ち新端末あり → triage を強制 (TOTP 有無問わず)。
    if (approvalTrusted && devicePending.length > 0) {
      return (
        <DeviceApprovalPromptModal
          open
          pending={devicePending}
          onChanged={() => refreshPending()}
        />
      )
    }
  }

  // enrollment check loading 中は children を通す (= 一瞬の loading flash 回避)。
  // すぐ requiresEnrollment=true に確定すれば modal が覆い被さる。
  if (!enLoading && requiresEnrollment) {
    // #815: WebAuthn 不可ブラウザ (#787) かつ TOTP fallback flag ON のときだけ
    // TOTP enroll に誘導（passkey を作れず gate を満たせない層の救済）。
    // それ以外（capable / flag OFF）は従来どおり PasskeyEnrollmentModal で
    // byte-for-byte 不変（flag OFF 時は不可層も従来の unsupported 警告のまま＝回帰なし）。
    const totpFallbackOn = config?.totpFallbackEnabled === true
    // #815: WebAuthn 不可ブラウザ (#787) は TOTP へ。
    // #838: capable だが passkey ceremony が失敗し escape を選んだ層も TOTP へ。
    // どちらも totpFallbackEnabled ON のときだけ(OFF=従来 PasskeyModal で不変)。
    if ((!supported || escapedToTotp) && totpFallbackOn) {
      return <TotpEnrollmentModal open onEnrolled={() => markTotpEnrolled()} />
    }
    return (
      <PasskeyEnrollmentModal
        open
        enforced
        onClose={() => {
          // enforced だが念のため (= 通常は onEnrolled で markEnrolled 経由で自動 unmount)
        }}
        onEnrolled={() => {
          markEnrolled()
        }}
        // #838: flag ON のときだけ escape を渡す。PasskeyModal は passkey
        // ceremony 失敗後にのみこの導線を表示(#789: 事前対等選択にしない)。
        onFallbackToTotp={
          totpFallbackOn ? () => setEscapedToTotp(true) : undefined
        }
      />
    )
  }

  // 国籍が未回答なら、埋まるまでプロフィール登録に戻す。
  //
  // #2005 で登録時の既定値 ('US') をやめたので、答えずに離脱した人は空のまま残る。
  // `/setup/profile` へ送るのはログイン直後の 1 回だけで、そこから抜けると二度と
  // 聞かれなかった。答えたくない人には「その他」があるので、逃げ道は塞がない。
  //
  // **`user` が入るまで判定しない。** アプリを開き直した直後は復元中で user が
  // null、国籍も空に見える。そこで弾くと**完了済みの人まで毎回ここへ飛ばされる**。
  const needsNationality = user != null && user.nationality === ''
  const gateExempt = PROFILE_GATE_EXEMPT.some((prefix) => location.pathname.startsWith(prefix))
  if (needsNationality && !gateExempt) {
    // 聞き終わったら元の場所へ戻す (スタンプの QR を読もうとした人を /home に
    // 落とすと、会場でもう一度たどり直すことになる)
    sessionStorage.setItem(PROFILE_GATE_REDIRECT_KEY, location.pathname + location.search)
    return <Navigate to="/setup/profile" replace />
  }

  return <>{children}</>
}
